/**
 * Multi-language translation utility
 *
 * Selects the appropriate translation for the user's language preference
 * from the multi-language translations JSONB object.
 *
 * Falls back to the single-language `translation` field for backward compatibility.
 */

/**
 * Get translation for a specific language from a multi-language object.
 *
 * @param translations - Multi-language JSONB object { "zh-TW": "...", "ja": "...", ... }
 * @param lang - Target language code (e.g., 'zh-TW', 'ja', 'ko')
 * @param fallback - Fallback string (usually the single-language `translation` field)
 * @returns The translation for the target language, or the fallback
 */
export function getTranslation(
    translations: Record<string, string> | null | undefined,
    lang: string,
    fallback = '',
): string {
    if (!translations) return fallback;
    return translations[lang] || fallback;
}

/**
 * Get translation for a segment-like object (with both `translation` and `translations` fields).
 *
 * @param item - Object with `translation` and optional `translations` fields
 * @param lang - Target language code
 * @returns The best available translation
 */
export function getSegmentTranslation(
    item: { translation?: string; translations?: Record<string, string> | null },
    lang: string,
): string {
    return getTranslation(item.translations, lang, item.translation || '');
}

/**
 * Get native-language field value from a multi-language object.
 * Used for fields like `explanation_native` / `explanation_natives`.
 *
 * @param natives - Multi-language JSONB object { "zh-TW": "...", "ja": "...", ... }
 * @param lang - Target language code
 * @param fallbackNative - Fallback single-language native field value
 * @returns The native text for the target language
 */
export function getNativeText(
    natives: Record<string, string> | null | undefined,
    lang: string,
    fallbackNative = '',
): string {
    if (!natives) return fallbackNative;
    return natives[lang] || fallbackNative;
}
